'use client';

import { Message } from '@/lib/copilot/message';
import { Link as CopilotLink, LinkGroup } from '@/lib/copilot/types';
import classNames from 'classnames';
import { useState, ReactNode, useRef, memo } from 'react';
import { useChat } from '@/contexts/ChatProvider';
import { parse } from 'marked';
import { Body } from '@/components/wysiwyg/Body';
import Link from 'next/link';
import { 
  ArrowPathIcon, 
  ClipboardDocumentCheckIcon, 
  ClipboardDocumentListIcon, 
  HandThumbDownIcon, 
  HandThumbUpIcon 
} from '@heroicons/react/24/outline';
import { sendGTMEvent } from '@next/third-parties/google';
import { AcquiaFeedback } from './AcquiaFeedback.client';
import { ProductLogo } from '@/components/assets/ProductLogo';
import { useProducts } from '@/contexts/ProductsProvider';
import { DrupalTaxonomyTermProduct } from '@/types';

type AssistantMessageProps = {
  message: Message;
  isLast: boolean;
};

type AssistantMessageOptionsProps = {
  children: ReactNode;
  message: Message;
  isLast: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
};

/**
 * Provides controls for the assistant message (copy, like/dislike, retry)
 */
function AssistantMessageOptions({ children, message, isLast, contentRef }: AssistantMessageOptionsProps) {
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState<boolean>();
  const [retried, setRetry] = useState<boolean>();
  const { retry } = useChat();

  // If the message is still being generated, don't show controls
  if (message.status === 'in_progress') {
    return <>{children}</>;
  }

  const voted = liked !== undefined;
  const buttonClass = 'p-2 rounded-lg transition-all duration-300 hover:bg-gray-400';
  
  const handleRetry = () => {
    retry();
    setRetry(true);
  };

  const handleCopy = () => {
    navigator.clipboard
      .write([
        new ClipboardItem({
          ['text/html']: new Blob([contentRef.current?.innerHTML ?? ''], { type: 'text/html' }),
        }),
      ])
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch((err) => {
        setCopied(false);
        console.error(err);
      });
  };

  const handleVote = (vote?: boolean) => {
    setLiked(vote);
    if (vote !== undefined) {
      sendGTMEvent({
        event: vote ? 'chatLike' : 'chatDislike',
        value: true,
      });
    }
  };

  return (
    <div className="flex flex-col gap-2 group relative">
      {children}
      <div className="flex group-hover:flex bg-white rounded-lg border border-gray-600 p-1 items-center gap-2 text-navy-600 shadow w-fit">
        {/* Copy button */}
        <button onClick={handleCopy} className={buttonClass}>
          {copied ? (
            <>
              <ClipboardDocumentCheckIcon className="w-5" aria-hidden="true" />
              <span className="sr-only">Copied!</span>
            </>
          ) : (
            <>
              <ClipboardDocumentListIcon className="w-5" aria-hidden="true" />
              <span className="sr-only">Copy</span>
            </>
          )}
        </button>
        
        <span className="border-s-2 border-gray-600">&nbsp;</span>
        
        {/* Like button */}
        <button
          className={classNames(
            buttonClass,
            voted && liked ? 'text-teal-600' : 'disabled:text-gray-800 disabled-hover:border-transparent'
          )}
          disabled={voted && !liked}
          onClick={() => handleVote(voted ? undefined : true)}
        >
          <HandThumbUpIcon className="w-5" aria-hidden="true" />
          <span className="sr-only">Like</span>
        </button>
        
        {/* Dislike button */}
        <button
          className={classNames(
            buttonClass,
            voted && !liked ? 'text-pink-600' : 'disabled:text-gray-800 disabled-hover:border-transparent'
          )}
          disabled={voted && liked}
          onClick={() => handleVote(voted ? undefined : false)}
        >
          <HandThumbDownIcon className="w-5" aria-hidden="true" />
          <span className="sr-only">Dislike</span>
          {voted && !liked && <AcquiaFeedback />}
        </button>
        
        {/* Retry button (only shown for the last message) */}
        {isLast && (
          <>
            <span className="border-s-2 border-gray-600">&nbsp;</span>
            <button 
              title="Retry" 
              onClick={handleRetry} 
              className={classNames(buttonClass, 'flex items-center gap-2')}
            >
              <ArrowPathIcon 
                className={classNames('w-5', retried ? 'animate-spin' : '')} 
                title="Retry" 
                aria-hidden="true" 
              />
              <span className="sr-only">Retry</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

type MessageLinksProps = {
  links?: Array<CopilotLink | LinkGroup>;
};

/**
 * Renders related links provided in the assistant's response
 */
function MessageLinksComponent({ links }: MessageLinksProps) {
  if (!links || links.length === 0) {
    return null;
  }
  
  return (
    <div className="border border-gray-400 bg-gray-300 text-teal-800 mr-auto p-4 rounded-lg w-fit flex flex-col gap-2">
      {links.map((link) => {
        if ('links' in link) {
          // This is a link group
          const group = link as LinkGroup;
          if (group.links.length === 0) {
            return null;
          }
          
          return (
            <div key={group.id} className="flex flex-col gap-2">
              <h4 className="font-medium">{group.title}</h4>
              <MessageLinks links={group.links} />
            </div>
          );
        } else {
          // This is an individual link
          const copilotLink = link as CopilotLink;
          return (
            <div key={copilotLink.href} className="pl-2 flex flex-col gap-1">
              <Link 
                className="hover:text-blue-600 hover:underline flex items-center gap-2"
                onClick={() => {
                  sendGTMEvent({
                    event: 'copilotLinkClick',
                    value: copilotLink.href,
                  });
                }}
                target={copilotLink.target} 
                href={copilotLink.href}
              >
                {copilotLink.className !== undefined && <FromProductLogo productName={copilotLink.className} />}
                {copilotLink.title}
              </Link>
            </div>
          );
        }
      })}
    </div>
  );
}

const MessageLinks = memo(MessageLinksComponent);

/**
 * Displays a product logo based on the product name
 */
function FromProductLogoComponent({ productName }: { productName: string }) {
  const { products } = useProducts();
  const product = products.reduce<DrupalTaxonomyTermProduct | null>(
    (r, c) => (c.name === productName ? c : r), 
    null
  );

  if (!product) {
    return null;
  }

  return (
    <span role="none" className="flex gap-1 items-end font-medium">
      <ProductLogo title={productName} name={product.field_product_logo} className="w-4 pb-1" />
    </span>
  );
}

const FromProductLogo = memo(FromProductLogoComponent);

/**
 * Renders messages from the assistant/agent with controls for feedback and copying
 */
function AssistantMessageComponent({ message, isLast }: AssistantMessageProps) {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <AssistantMessageOptions message={message} isLast={isLast} contentRef={ref}>
      <div 
        className={classNames(
          'bg-gray-400 text-black mr-auto',
          'p-4 rounded-lg w-fit'
        )}
      >
        <p className="sr-only">Acquia Copilot: </p>
        <div ref={ref} className="max-w-lg">
          <Body value={parse(message.content ?? '', {async: false}) as string} />
        </div>
        <MessageLinks links={message.links} />
      </div>
    </AssistantMessageOptions>
  );
}

export const AssistantMessage = memo(AssistantMessageComponent);